import React, { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import type { ReasonPreset } from '@/utils/confirmService';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  /** Skrevet bekreftelse: bekreft-knappen er låst til teksten matcher nøyaktig. */
  typeToConfirm?: string;
  /** Valgfri slettegrunn med hurtigvalg + fritekst. */
  reasonPresets?: ReasonPreset[];
  reasonLabel?: string;
  reasonRequired?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open, title, description, confirmLabel, cancelLabel, variant = 'default',
  typeToConfirm, reasonPresets, reasonLabel, reasonRequired = false,
  onConfirm, onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const confirmText = confirmLabel || t('common.confirm', 'Bekreft');
  const cancelText = cancelLabel || t('common.cancel', 'Avbryt');
  const reasonText = reasonLabel || t('confirmDialog.slettegrunnValgfritt', 'Slettegrunn (valgfritt)');
  const [typed, setTyped] = useState('');
  const [presetKey, setPresetKey] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');

  useEffect(() => {
    if (open) { setTyped(''); setPresetKey(null); setFreeText(''); }
  }, [open]);

  const preset = reasonPresets?.find(p => p.key === presetKey);
  const reason = presetKey && presetKey !== 'other' && preset
    ? (freeText.trim() ? `${preset.label}: ${freeText.trim()}` : preset.label)
    : freeText.trim();
  const typedOk = !typeToConfirm || typed.trim().toUpperCase() === typeToConfirm.toUpperCase();
  const reasonOk = !reasonRequired || reason.length > 0;
  const canConfirm = typedOk && reasonOk;
  const submit = () => { if (canConfirm) onConfirm(reason); };

  // Enter = confirm, Escape = cancel. Capture phase so it wins over any
  // modal's global key handler that is open underneath.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onCancel(); }
      else if (e.key === 'Enter') {
        // Don't let Enter typed inside a form control (in a form underneath)
        // accidentally confirm a destructive action.
        const tgt = e.target as HTMLElement | null;
        const tag = tgt?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tgt?.isContentEditable) return;
        e.preventDefault(); e.stopPropagation(); submit();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onCancel, canConfirm, reason]);

  if (!open) return null;

  // `pointer-events-auto` is required because Radix Dialog/Sheet sets
  // pointer-events:none on <body>; without it this confirm (rendered at root,
  // outside the sheet subtree) would inherit none and be unclickable.
  return (
    <div className="fixed inset-0 z-[1000000] flex items-center justify-center bg-black/50 pointer-events-auto" data-testid="confirm-dialog-overlay" onClick={onCancel}>
      <div className="bg-background rounded-lg shadow-xl max-w-md w-full mx-4 p-6 pointer-events-auto" onClick={e => e.stopPropagation()} data-testid="confirm-dialog">
        <div className="flex items-start gap-3">
          {variant === 'danger' && (
            <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{description}</p>
          </div>
        </div>

        {reasonPresets && reasonPresets.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="confirm-reason-block">
            <p className="text-xs font-medium text-muted-foreground">{reasonText}</p>
            <div className="flex flex-wrap gap-1.5">
              {reasonPresets.map(p => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPresetKey(presetKey === p.key ? null : p.key)}
                  data-testid={`confirm-reason-${p.key}`}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    presetKey === p.key
                      ? 'bg-foreground text-background border-foreground'
                      : 'bg-muted/40 text-foreground border-border hover:bg-muted'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Input
              value={freeText}
              onChange={e => setFreeText(e.target.value)}
              placeholder={presetKey === 'other' || !presetKey ? t('confirmDialog.skrivKortBegrunnelse', 'Skriv en kort begrunnelse …') : t('confirmDialog.utdypValgfritt', 'Utdyp (valgfritt) …')}
              className="h-8 text-sm"
              data-testid="confirm-reason-input"
            />
          </div>
        )}

        {typeToConfirm && (
          <div className="mt-4 space-y-1.5" data-testid="confirm-typed-block">
            <p className="text-xs text-muted-foreground">
              <Trans i18nKey="confirmDialog.skrivForABekrefte" values={{ text: typeToConfirm }} defaults="Skriv <1>{{text}}</1> for å bekrefte:" components={{ 1: <span className="font-mono font-semibold text-foreground select-all" /> }} />
            </p>
            <Input
              autoFocus
              value={typed}
              onChange={e => setTyped(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
              placeholder={typeToConfirm}
              className={`h-9 font-mono ${typed && !typedOk ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
              autoComplete="off"
              data-testid="confirm-typed-input"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="confirm-dialog-cancel">
            {cancelText}
          </Button>
          <Button
            size="sm"
            disabled={!canConfirm}
            className={variant === 'danger' ? 'bg-red-600 hover:bg-red-700 text-white disabled:opacity-40' : ''}
            onClick={submit}
            data-testid="confirm-dialog-confirm"
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}
